import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { unwrapApiArray } from "../../shared/utils/apiResponse";
import { handleGetPublicGames, handleMatchVerification } from "../../userDashboard/userControllers/gameController";
import Banner from "../components/Banner";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import landingAsset from "../utils/assetPaths";

interface GameInfo {
    id: string | number;
    title: string;
    type?: string;
}

const EmailVerify: React.FC = () => {
    const { id } = useParams();
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [games, setGames] = useState<GameInfo[]>([]);

    useEffect(() => {
        const doVerify = async () => {
            if (!id) return;
            try {
                await handleMatchVerification(
                    id,
                    () => {
                        setLoading(false);
                        enqueueSnackbar("Verification Successful!", { variant: "success" });
                    },
                    () => {
                        setLoading(false);
                        enqueueSnackbar("Verification Failed.", { variant: "error" });
                    }
                );
            } catch (error) {
                setLoading(false);
            }
        };
        doVerify();

        const fetchGames = async () => {
            try {
                const data = await handleGetPublicGames<GameInfo[]>();
                setGames(unwrapApiArray<GameInfo>(data));
            } catch (error) {
                console.error(error);
            }
        };
        fetchGames();
    }, [id, enqueueSnackbar]);

    return (
        <>
            <Navbar />
            <div
                style={{
                    backgroundImage: `url(${landingAsset("herobg.svg")})`,
                    backgroundSize: "cover",
                }}
                className="h-[500px] md:h-[400px] relative w-full flex justify-center items-center flex-col "
            >
                <div
                    style={{
                        background:
                            "linear-gradient(180deg, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0) 17.83%)",
                    }}
                    className=" h-full w-full absolute left-0 top-0 text-center flex flex-col items-center justify-center p-4"
                >
                    <h1 className="text-white text-4xl md:text-5xl lg:text-6xl font-black font-Manrope uppercase tracking-tight">Identity Verification</h1>
                    <p className=" mt-6 text-center font-Manrope text-lg md:text-xl text-white/90 max-w-2xl leading-relaxed">
                        {loading ? "Please wait while we confirm your registration status..." : "Verification process complete. Check the results below."}
                    </p>
                </div>
            </div>

            <div className="flex flex-col items-center py-24 w-full px-4 md:px-14 bg-gray-50 min-h-[500px]">
                <div className="max-w-2xl w-full mx-auto">
                    <div className="bg-white p-16 rounded-[48px] shadow-2xl border border-gray-100 text-center">
                        <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-10 text-4xl animate-pulse">
                            {loading ? "⏳" : "✅"}
                        </div>
                        <h2 className="text-3xl font-black text-gray-900 mb-4">{loading ? "Verifying Token..." : "Status Confirmed"}</h2>
                        <p className="text-gray-500 font-bold mb-12 italic text-lg leading-relaxed">
                            {loading
                                ? "Our secure servers are processing your request. This may take a few seconds."
                                : "Your identity has been successfully verified. You are now cleared for match attendance."}
                        </p>
                        {!loading && (
                            <button onClick={() => navigate("/matches")} className="w-full bg-green-700 text-white py-5 rounded-2xl font-black text-xl shadow-xl hover:shadow-2xl transition-all active:scale-95">Explore More Matches</button>
                        )}
                    </div>
                </div>
            </div>

            <div className="py-24 bg-white px-4 md:px-14">
                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-10 mb-16">
                        <div className="space-y-4">
                            <h3 className="text-green-700 font-black uppercase tracking-[0.2em] text-sm">Join the Community</h3>
                            <h2 className="text-4xl font-black text-gray-900">Recommended Matches</h2>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
                        {games.slice(0, 3).map((game, i) => (
                            <div key={i} className="bg-gray-50 rounded-[40px] p-8 border border-gray-100 group hover:shadow-2xl transition-all duration-500">
                                <p className="font-black text-green-700 mb-6 uppercase tracking-widest text-xs">{game.type || "Match"}</p>
                                <h4 className="text-2xl font-black text-gray-900 mb-8">{game.title}</h4>
                                <button onClick={() => navigate(`/matches/${game.id}`)} className="text-gray-900 font-black flex items-center gap-2 hover:text-green-700 transition-colors uppercase tracking-widest text-sm">View Details →</button>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <Banner />
            <Footer />
        </>
    );
};

export default EmailVerify;
